import React, { useEffect, useMemo, useState } from 'react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { ComparisonChartRow, PositionExperienceRow } from '../types';
import { useMediaQuery } from '../hooks/useMediaQuery';
import {
    buildComparisonRows,
    getPositionOptions,
    getPreferredPositions,
    shortExperience,
} from '../utils/dataTransforms';
import {
    formatCompactSalary,
    formatSalary,
    formatSample,
} from '../utils/formatters';
import AccessibleDataTable from './AccessibleDataTable';
import ChartTooltip from './ChartTooltip';
import SelectField from './SelectField';

interface PositionComparisonProps {
    rows: PositionExperienceRow[];
    selectedYear: number;
}

interface ComparisonTooltipProps {
    active?: boolean;
    payload?: any[];
    positionA: string;
    positionB: string;
}

const ComparisonTooltip: React.FC<ComparisonTooltipProps> = ({
    active,
    payload,
    positionA,
    positionB,
}) => {
    if (!active || !payload?.length) {
        return null;
    }

    const row = payload[0].payload as ComparisonChartRow;
    const salaryA =
        row.salaryA === null ? 'Veri yok' : formatSalary(row.salaryA);
    const salaryB =
        row.salaryB === null ? 'Veri yok' : formatSalary(row.salaryB);
    const sampleA =
        row.sampleA === null ? '' : ` · ${formatSample(row.sampleA)}`;
    const sampleB =
        row.sampleB === null ? '' : ` · ${formatSample(row.sampleB)}`;

    return (
        <ChartTooltip
            title={row.experience}
            rows={[
                {
                    label: positionA,
                    value: `${salaryA}${sampleA}`,
                    tone: 'primary',
                },
                {
                    label: positionB,
                    value: `${salaryB}${sampleB}`,
                    tone: 'secondary',
                },
            ]}
        />
    );
};

const PositionComparison: React.FC<PositionComparisonProps> = ({
    rows,
    selectedYear,
}) => {
    const yearRows = useMemo(
        () => rows.filter((row) => row.year === selectedYear),
        [rows, selectedYear]
    );
    const options = useMemo(() => getPositionOptions(yearRows), [yearRows]);
    const preferred = useMemo(
        () => getPreferredPositions(yearRows),
        [yearRows]
    );
    const [positionA, setPositionA] = useState(preferred[0] ?? '');
    const [positionB, setPositionB] = useState(preferred[1] ?? '');
    const mobile = useMediaQuery('(max-width: 680px)');
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    useEffect(() => {
        if (!options.includes(positionA)) {
            setPositionA(preferred[0] ?? options[0] ?? '');
        }
        if (!options.includes(positionB) || positionA === positionB) {
            setPositionB(
                preferred.find((position) => position !== positionA) ??
                    options.find((position) => position !== positionA) ??
                    ''
            );
        }
    }, [options, positionA, positionB, preferred]);

    const chartRows = useMemo(
        () => buildComparisonRows(yearRows, positionA, positionB),
        [positionA, positionB, yearRows]
    );

    return (
        <>
            <div className="comparison-controls">
                <SelectField
                    id="comparison-position-a"
                    label="Pozisyon A"
                    value={positionA}
                    onChange={(event) => setPositionA(event.target.value)}
                >
                    {options.map((option) => (
                        <option
                            key={option}
                            value={option}
                            disabled={option === positionB}
                        >
                            {option}
                        </option>
                    ))}
                </SelectField>
                <div className="comparison-mark" aria-hidden="true">
                    karşı
                </div>
                <SelectField
                    id="comparison-position-b"
                    label="Pozisyon B"
                    value={positionB}
                    onChange={(event) => setPositionB(event.target.value)}
                >
                    {options.map((option) => (
                        <option
                            key={option}
                            value={option}
                            disabled={option === positionA}
                        >
                            {option}
                        </option>
                    ))}
                </SelectField>
            </div>

            <div className="comparison-legend" aria-hidden="true">
                <span>
                    <i className="legend-dot legend-dot--primary" />
                    {positionA}
                </span>
                <span>
                    <i className="legend-dot legend-dot--secondary" />
                    {positionB}
                </span>
            </div>

            <div
                className="chart-panel chart-panel--comparison"
                role="img"
                aria-label={`${positionA} ve ${positionB} için deneyime göre medyan maaş karşılaştırması`}
            >
                <div className="chart-canvas" aria-hidden="true">
                    <ResponsiveContainer width="100%" height={450}>
                        <LineChart
                            data={chartRows}
                            margin={{
                                top: 24,
                                right: mobile ? 8 : 24,
                                bottom: 12,
                                left: 4,
                            }}
                        >
                            <CartesianGrid
                                vertical={false}
                                stroke="#d9d5ca"
                                strokeDasharray="2 5"
                            />
                            <XAxis
                                dataKey="experience"
                                axisLine={false}
                                tickLine={false}
                                interval={0}
                                tickFormatter={
                                    mobile
                                        ? shortExperience
                                        : (value) => value
                                }
                                tick={{
                                    fill: '#5e655f',
                                    fontSize: mobile ? 9 : 11,
                                    fontWeight: 500,
                                }}
                                dy={10}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={formatCompactSalary}
                                tick={{
                                    fill: '#737a74',
                                    fontSize: mobile ? 9 : 10,
                                    fontFamily: 'DM Mono, monospace',
                                }}
                                width={mobile ? 56 : 70}
                            />
                            <Tooltip
                                cursor={{
                                    stroke: '#a9ada8',
                                    strokeDasharray: '3 4',
                                }}
                                content={
                                    <ComparisonTooltip
                                        positionA={positionA}
                                        positionB={positionB}
                                    />
                                }
                            />
                            <Line
                                type="monotone"
                                dataKey="salaryA"
                                name={positionA}
                                stroke="#264338"
                                strokeWidth={3}
                                connectNulls={false}
                                dot={{
                                    r: 4,
                                    fill: '#f8f6ef',
                                    stroke: '#264338',
                                    strokeWidth: 2,
                                }}
                                activeDot={{ r: 6 }}
                                isAnimationActive={!reduceMotion}
                                animationDuration={700}
                            />
                            <Line
                                type="monotone"
                                dataKey="salaryB"
                                name={positionB}
                                stroke="#c85e38"
                                strokeWidth={3}
                                connectNulls={false}
                                dot={{
                                    r: 4,
                                    fill: '#f8f6ef',
                                    stroke: '#c85e38',
                                    strokeWidth: 2,
                                }}
                                activeDot={{ r: 6 }}
                                isAnimationActive={!reduceMotion}
                                animationDuration={700}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
                <AccessibleDataTable
                    caption={`${positionA} ve ${positionB} deneyim maaş karşılaştırması`}
                    headers={[
                        'Deneyim',
                        `${positionA} medyan`,
                        `${positionA} örneklem`,
                        `${positionB} medyan`,
                        `${positionB} örneklem`,
                    ]}
                    rows={chartRows.map((row) => [
                        row.experience,
                        row.salaryA === null
                            ? 'Veri yok'
                            : formatSalary(row.salaryA),
                        row.sampleA === null
                            ? 'Veri yok'
                            : formatSample(row.sampleA),
                        row.salaryB === null
                            ? 'Veri yok'
                            : formatSalary(row.salaryB),
                        row.sampleB === null
                            ? 'Veri yok'
                            : formatSample(row.sampleB),
                    ])}
                />
            </div>
        </>
    );
};

export default React.memo(PositionComparison);
